import { EdgeDefinitionType } from './types'

/** returns canonical form for X has Group, note this is lowercase */
export const getObjectTypeHasGroupEdgeTypeId = (objectTypeId: string) => `${objectTypeId.toLowerCase()}_has_group`

/** returns a edgeTypeMatrix entry for a given Group - Class
 * If this is retro-fitted to existing datamodel edgeTypeMatrix, care (and unit tests) required not to change existing edgeTypeIds
 */
export const getGroupToTypeEdgeTypeMatrixEntry = <ObjectTypeId extends string>(objectTypeId: ObjectTypeId) => {
  const edgeTypeId = `group_has_${objectTypeId.toLowerCase()}`;

  const edgeDefinition: EdgeDefinitionType = {
    edgeTypeId,
    labels: {
      out: `has ${objectTypeId}`,
      in: `is ${objectTypeId} in group`,
    },
    cardinality: {
      out: null,
      in: null,
    },
  }

  const entry = {
    [objectTypeId]: {
      [edgeTypeId]: edgeDefinition,
    },
  } as Record<ObjectTypeId, { [x: string]: EdgeDefinitionType }>
  return entry
}

export const getEntityToDeviceEdgeTypeId = (enityType: string) => `${enityType.toLowerCase()}_has_Device`

export const getAreaToEntityMatrixEntry = <ObjectTypeId extends string>(objectTypeId: ObjectTypeId) => {
  const edgeTypeId = `area_has_${objectTypeId.toLowerCase()}`

  const edgeDefinition: EdgeDefinitionType = {
    edgeTypeId,
    labels: {
      out: `has ${objectTypeId}`,
      in: `is ${objectTypeId} in area`,
    },
    cardinality: {
      out: null,
      in: null,
    },
  }

  const entry = {
    [objectTypeId]: {
      [edgeTypeId]: edgeDefinition,
    },
  } as Record<ObjectTypeId, { [x: string]: EdgeDefinitionType }>
  return entry
}

export const getEntityToDeviceEdgeTypeMatrixEntryNamed = <ObjectTypeId extends string>(enityType: string) => {
  const edgeTypeId = getEntityToDeviceEdgeTypeId(enityType)

  const edgeDefinition: EdgeDefinitionType = {
    edgeTypeId,
    labels: {
      out: `has Device`,
      in: `is Device for ${enityType}`,
    },
    cardinality: {
      out: null,
      in: 1,
    },
  }
  const entry = {
    Device: {
      [edgeTypeId]: edgeDefinition,
    },
  } as Record<ObjectTypeId, { [x: string]: EdgeDefinitionType }>
  return { [enityType]: entry }
}

export const getEntityToDeviceEdgeTypeMatrixEntry = <ObjectTypeId extends string>(enityType: string) => {
  const edgeTypeId = getEntityToDeviceEdgeTypeId(enityType)

  const edgeDefinition: EdgeDefinitionType = {
    edgeTypeId,
    labels: {
      out: `has Device`,
      in: `is Device for ${enityType}`,
    },
    cardinality: {
      out: null,
      in: 1,
    },
  }
  const entry = {
    Device: {
      [edgeTypeId]: edgeDefinition,
    },
  } as Record<ObjectTypeId, { [x: string]: EdgeDefinitionType }>
  return entry
}

export const getParentToChildEdgeTypeMatrixEntry = <ObjectTypeId extends string>(parentId: string, childId: string) => {
  const edgeTypeId = `${parentId}_has_${childId}`.toLowerCase()

  const edgeDefinition: EdgeDefinitionType = {
    edgeTypeId,
    labels: {
      out: `has ${childId}`,
      in: `is ${childId} in ${parentId}`,
    },
    cardinality: {
      out: null,
      in: null,
    },
  }

  const entry = {
    [childId]: {
      [edgeTypeId]: edgeDefinition,
    },
  } as Record<ObjectTypeId, { [x: string]: EdgeDefinitionType }>
  return entry
}

export const getGroupToEntityEdgeTypeId = (objectTypeId: string) => `group_has_${objectTypeId}`

export const getEntityToPlaceEdgeTypeId = (enityType: string) => `${enityType.toLowerCase()}_has_Place`

export const getEntityToPlaceEdgeTypeMatrixEntry = <ObjectTypeId extends string>(enityType: string) => {
  const edgeTypeId = getEntityToPlaceEdgeTypeId(enityType)

  const edgeDefinition: EdgeDefinitionType = {
    edgeTypeId,
    labels: {
      out: `has Place`,
      in: `is Place for ${enityType}`,
    },
    cardinality: {
      out: null,
      in: null,
    },
  }

  const entry = {
    Place: {
      [edgeTypeId]: edgeDefinition,
    },
  } as Record<ObjectTypeId, { [x: string]: EdgeDefinitionType }>
  return entry
}

export const getPropertyToEntityEdgeTypeMatrixEntry = <ObjectTypeId extends string>(objectTypeId: string) => {
  const edgeTypeId = `property_has_${objectTypeId.toLowerCase()}`;

  const edgeDefinition: EdgeDefinitionType = {
    edgeTypeId,
    labels: {
      out: `has ${objectTypeId}`,
      in: `is ${objectTypeId} in property`,
    },
    cardinality: {
      out: null,
      in: null,
    },
  }

  const entry = {
    [objectTypeId]: {
      [edgeTypeId]: edgeDefinition,
    },
  } as Record<ObjectTypeId, { [x: string]: EdgeDefinitionType }>
  return entry
}

export const getEntityProgrammneToEntityEdgeTypeId = (objectTypeId: string) => `programme_schedules_${objectTypeId}`

export const getEntityProgrammneToEntityEdgeTypeMatrixEntry = <ObjectTypeId extends string>(objectTypeId: string) => {
  const edgeTypeId = getEntityProgrammneToEntityEdgeTypeId(objectTypeId)

  const edgeDefinition: EdgeDefinitionType = {
    edgeTypeId,
    labels: {
      out: `schedules`,
      in: `is scheduled by`,
    },
    cardinality: {
      out: null,
      in: null,
    },
  }

  const entry = {
    [objectTypeId]: {
      [edgeTypeId]: edgeDefinition,
    },
  } as Record<ObjectTypeId, { [x: string]: EdgeDefinitionType }>
  return entry
}

export const getEntityRollingProgrammeToEntityEdgeTypeId = (objectTypeId: string) => `rolling_programme_schedules_${objectTypeId}`

export const getEntityRollingProgrammneToEntityEdgeTypeMatrixEntry = <ObjectTypeId extends string>(objectTypeId: string) => {
  const edgeTypeId = getEntityRollingProgrammeToEntityEdgeTypeId(objectTypeId)

  const edgeDefinition: EdgeDefinitionType = {
    edgeTypeId,
    labels: {
      out: 'schedules',
      in: 'is scheduled by Rolling Programme',
    },
    cardinality: {
      out: null,
      in: null,
    },
  }

  const entry = {
    [objectTypeId]: {
      [edgeTypeId]: edgeDefinition,
    },
  } as Record<ObjectTypeId, { [x: string]: EdgeDefinitionType }>
  return entry
}

export const getOnDemandProgrammeToEntityEdgeTypeId = (objectTypeId: string) => `on_demand_programme_sequences_${objectTypeId}`
